import {Event} from '@/apiTypes/event';
import {GroupedEventList} from '@/components/groupedEventList/groupedEventList';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {Button} from '@/components/ui/button';
import {Card, CardContent} from '@/components/ui/card';
import {Typography} from '@/components/ui/typography';
import {getOrganizerEvents} from '@/server/organizer';
import {Link, createFileRoute, getRouteApi} from '@tanstack/react-router';
import {Plus} from 'lucide-react';
import {FC} from 'react';
import {z} from 'zod';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://wayra.cc';
const DEFAULT_PAGE_SIZE = 12;

const parentRouteApi = getRouteApi('/organizer/$organizerId');

export const Route = createFileRoute('/organizer/$organizerId/')({
  validateSearch: z.object({
    page: z.number().int().nonnegative().optional().catch(undefined),
    pageSize: z.number().int().nonnegative().optional().catch(undefined),
  }),
  loaderDeps: ({search: {page, pageSize}}) => ({
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
  }),
  loader: async ({params, deps}) => {
    const eventsResult = await getOrganizerEvents({
      data: {
        organizerId: params.organizerId,
        page: deps.page,
        pageSize: deps.pageSize,
      },
    });
    return eventsResult;
  },
  head: ({params, loaderData}) => {
    if (!loaderData) return {};
    return {
      links: [
        {rel: 'canonical', href: `${SITE_URL}/organizer/${params.organizerId}`},
      ],
    };
  },
  component: OrganizerEventsTab,
});

interface UpcomingEventsSectionProps {
  events: Event[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  organizerName: string;
  isAdmin: boolean;
}

/** Paginated grid of upcoming events */
const UpcomingEventsSection: FC<UpcomingEventsSectionProps> = ({
  events,
  pagination,
  organizerName,
  isAdmin,
}) => {
  return (
    <div>
      <div className="mb-6">
        <Typography level="H5" className="font-bold">
          Upcoming Events
        </Typography>
        <Typography level="B3" className="mt-1 text-neutral-600">
          {pagination.totalCount}{' '}
          {pagination.totalCount === 1 ? 'event' : 'events'} from{' '}
          {organizerName}
        </Typography>
      </div>

      {events.length > 0 ? (
        <>
          <GroupedEventList events={events} />

          {pagination.totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <PaginationGroup pagination={pagination} />
            </div>
          )}
        </>
      ) : (
        <Card className="border border-neutral-200">
          <CardContent className="px-6 py-12 text-center">
            <Typography level="B1" className="mb-4 text-neutral-500">
              No upcoming events from this organizer.
            </Typography>
            {isAdmin && (
              <Link to="/create-event">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Event
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Events tab — reads events from its own loader, organizer/isAdmin from the parent layout loader. The parent layout owns the page wrapper and sidebar. */
function OrganizerEventsTab() {
  const {events, pagination} = Route.useLoaderData();
  const {organizer, isAdmin} = parentRouteApi.useLoaderData();

  return (
    <UpcomingEventsSection
      events={events}
      pagination={pagination}
      organizerName={organizer.name}
      isAdmin={isAdmin}
    />
  );
}
